import { useState, useRef } from "react";
import { toast } from "sonner";
import { UploadCloud, Loader2, X } from "lucide-react";
import { api, API } from "@/lib/api";

/**
 * Drag-drop video uploader. Uploads via /api/admin/upload and returns the
 * absolute video URL via the `onUploaded(url)` callback.
 *
 * Props:
 *   value        current video URL to preview (or empty)
 *   onUploaded   (url) => void — called after successful upload
 *   onCleared    () => void — called when user removes the video
 */
export default function VideoDropzone({ value = "", onUploaded, onCleared, label = "Drop a video or click to browse" }) {
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const inputRef = useRef(null);

  const resolveUrl = (rel) => (rel.startsWith("http") ? rel : `${API.replace(/\/api$/, "")}${rel}`);

  const handleFile = async (file) => {
    if (!file) return;
    if (!/^video\//.test(file.type)) {
      toast.error("Only video files are allowed");
      return;
    }
    if (file.size > 200 * 1024 * 1024) {
      toast.error("Max video size is 200 MB");
      return;
    }
    setUploading(true);
    setProgress(0);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await api.post("/admin/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => {
          if (e.total) setProgress(Math.round((e.loaded / e.total) * 100));
        },
      });
      const absUrl = resolveUrl(data.url);
      onUploaded?.(absUrl);
      toast.success("Video uploaded");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Upload failed";
      toast.error(msg);
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };

  return (
    <div>
      <div
        data-testid="video-dropzone"
        onClick={() => !uploading && inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        className={`relative border-2 border-dashed transition-colors cursor-pointer ${dragging ? "border-[#FF4500] bg-[#FF4500]/[0.06]" : "border-white/15 bg-[#0A0A0A]"} hover:border-white/40`}
      >
        {value ? (
          <div className="relative">
            <video
              src={value}
              controls
              preload="metadata"
              className="w-full max-h-72 object-contain bg-black"
              data-testid="video-dropzone-preview"
            />
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onCleared?.(); }}
              className="absolute top-2 right-2 w-8 h-8 bg-[#0A0A0A]/80 border border-white/20 flex items-center justify-center text-white/80 hover:text-[#FF4500] hover:border-[#FF4500]"
              aria-label="Remove video"
            >
              <X className="w-4 h-4" />
            </button>
            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#0A0A0A]/90 to-transparent p-3 text-center pointer-events-none">
              <p className="text-[10px] uppercase tracking-widest text-white/70">Click / drop to replace</p>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-12 px-6 text-center">
            {uploading ? (
              <>
                <Loader2 className="w-6 h-6 text-[#FF4500] animate-spin mb-3" />
                <p className="text-sm text-white/70">Uploading… {progress > 0 ? `${progress}%` : ""}</p>
                {progress > 0 && (
                  <div className="w-64 max-w-full h-1 bg-white/10 mt-3 overflow-hidden">
                    <div className="h-full bg-[#FF4500] transition-all" style={{ width: `${progress}%` }} />
                  </div>
                )}
              </>
            ) : (
              <>
                <UploadCloud className="w-8 h-8 text-white/60 mb-3" />
                <p className="text-sm text-white/80">{label}</p>
                <p className="text-[10px] uppercase tracking-widest text-white/40 mt-2">MP4 · MOV · WebM · Max 200 MB</p>
              </>
            )}
          </div>
        )}
        <input
          ref={inputRef}
          type="file"
          accept="video/*"
          className="hidden"
          disabled={uploading}
          onChange={(e) => handleFile(e.target.files?.[0])}
          data-testid="video-dropzone-input"
        />
      </div>
    </div>
  );
}
